import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import surveyApiService from '../../../services/surveyApi';

// A "class link" (postable to a group chat) can't carry one student's
// credential the way the personal magic link does, so opening it always
// requires being logged in as the enrolled student — this hook mirrors
// useAttendanceCheckIn's redirect-to-login-then-back pattern for that.
export function useSurveyClassLink() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolving, setResolving] = useState(false);

  // The non-identifying preview loads regardless of auth state, so the
  // "please log in" screen can say what the visitor is logging in for.
  useEffect(() => {
    (async () => {
      try {
        const res = await surveyApiService.getSurveyClassLinkPreview(token);
        if (res.success) {
          setPreview(res.data);
        } else {
          setError(res.message || 'This survey link is invalid or has been removed');
        }
      } catch (err) {
        setError(err.message || 'This survey link is invalid or has been removed');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Once auth state is known: a logged-in student gets routed straight to
  // their own assignment; a logged-in non-student sees a plain explanation
  // (this link has no meaning for an instructor/admin); a logged-out visitor
  // is left to the render below, which offers a login link.
  //
  // attemptedForRef guards against retrying after a terminal failure (e.g.
  // 403 "not enrolled in this class") — `resolving` alone isn't enough,
  // since setResolving(false) on error changes a value this effect also
  // depends on, re-firing it and retrying forever.
  const attemptedForRef = useRef(null);
  useEffect(() => {
    if (authLoading || !user || resolving) return;

    if (user.role !== 'student') {
      setError('This link opens a student\'s survey. If you\'re an instructor, view responses from the Surveys page instead.');
      return;
    }

    const attemptKey = `${token}:${user.id}`;
    if (attemptedForRef.current === attemptKey) return;
    attemptedForRef.current = attemptKey;

    setResolving(true);
    (async () => {
      try {
        const res = await surveyApiService.resolveSurveyClassLink(token);
        if (res.success) {
          navigate(`/student/surveys/${res.data.assignment_id}`, { replace: true });
        } else {
          setError(res.message || 'Unable to open this survey');
          setResolving(false);
        }
      } catch (err) {
        setError(err.message || 'Unable to open this survey');
        setResolving(false);
      }
    })();
  }, [authLoading, user, token, navigate, resolving]);

  const goToLogin = () => navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);

  return { authLoading, error, goToLogin, loading, preview, resolving, user };
}
